/**
 * Website Builder Pro - Advanced Wix-style Website Creator
 * Features: Templates, Multi-page Navigation, Fonts, Animations, Mascots, Backdrops
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Globe, FileText, Link2, Sparkles, ArrowRight, ArrowLeft,
  Check, Layout, Palette, Eye, Rocket, Plus, Trash2,
  Type, Image, Mail, Users, Star, Menu, ChevronDown,
  DollarSign, HelpCircle, BarChart3, Zap, Building2,
  Briefcase, Heart, ShoppingBag, GraduationCap, X, Wand2,
  Move, Layers, PaintBucket, Cat, Dog, Bot, Ghost,
  Smile, Crown, Settings, Grid3X3, MonitorSmartphone
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== VISUAL TEMPLATES ====================
const VISUAL_TEMPLATES = [
  {
    id: 'startup',
    name: 'Startup Launch',
    category: 'Business',
    preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Bold, modern design for tech startups',
    pages: ['home', 'features', 'pricing', 'contact'],
    style: { font: 'Inter', animation: 'fade-up', layout: 'centered' }
  },
  {
    id: 'agency',
    name: 'Creative Agency',
    category: 'Business',
    preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    description: 'Showcase your creative work',
    pages: ['home', 'portfolio', 'services', 'team', 'contact'],
    style: { font: 'Poppins', animation: 'slide-in', layout: 'asymmetric' }
  },
  {
    id: 'restaurant',
    name: 'Restaurant & Café',
    category: 'Food',
    preview: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    description: 'Warm, inviting design for eateries',
    pages: ['home', 'menu', 'about', 'gallery', 'reservations'],
    style: { font: 'Playfair Display', animation: 'fade', layout: 'elegant' }
  },
  {
    id: 'portfolio',
    name: 'Personal Portfolio',
    category: 'Personal',
    preview: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    description: 'Showcase your work and skills',
    pages: ['home', 'projects', 'about', 'contact'],
    style: { font: 'Space Grotesk', animation: 'zoom', layout: 'minimal' }
  },
  {
    id: 'ecommerce',
    name: 'Online Store',
    category: 'E-Commerce',
    preview: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    description: 'Sell products with style',
    pages: ['home', 'products', 'about', 'faq', 'contact'],
    style: { font: 'DM Sans', animation: 'fade-up', layout: 'grid' }
  },
  {
    id: 'nonprofit',
    name: 'Non-Profit',
    category: 'Organization',
    preview: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    description: 'Share your mission and impact',
    pages: ['home', 'mission', 'programs', 'donate', 'contact'],
    style: { font: 'Nunito', animation: 'slide-up', layout: 'warm' }
  },
  {
    id: 'saas',
    name: 'SaaS Product',
    category: 'Tech',
    preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    description: 'Professional software product page',
    pages: ['home', 'features', 'pricing', 'docs', 'contact'],
    style: { font: 'Inter', animation: 'fade-up', layout: 'tech' }
  },
  {
    id: 'fitness',
    name: 'Fitness & Gym',
    category: 'Health',
    preview: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
    description: 'Energetic design for fitness brands',
    pages: ['home', 'programs', 'trainers', 'schedule', 'contact'],
    style: { font: 'Montserrat', animation: 'zoom', layout: 'bold' }
  },
  {
    id: 'blank',
    name: 'Start Fresh',
    category: 'Custom',
    preview: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',
    description: 'Build from scratch with full control',
    pages: ['home'],
    style: { font: 'Inter', animation: 'fade', layout: 'minimal' }
  }
];

// ==================== AVAILABLE PAGES ====================
const AVAILABLE_PAGES = [
  { id: 'home', name: 'Home', icon: Globe, required: true },
  { id: 'about', name: 'About', icon: Users },
  { id: 'services', name: 'Services', icon: Star },
  { id: 'products', name: 'Products', icon: ShoppingBag },
  { id: 'portfolio', name: 'Portfolio', icon: Grid3X3 },
  { id: 'gallery', name: 'Gallery', icon: Image },
  { id: 'team', name: 'Team', icon: Users },
  { id: 'pricing', name: 'Pricing', icon: DollarSign },
  { id: 'faq', name: 'FAQ', icon: HelpCircle },
  { id: 'blog', name: 'Blog', icon: FileText },
  { id: 'testimonials', name: 'Testimonials', icon: Star },
  { id: 'contact', name: 'Contact', icon: Mail },
  { id: 'features', name: 'Features', icon: Zap },
  { id: 'menu', name: 'Menu', icon: Menu },
  { id: 'reservations', name: 'Reservations', icon: Check },
  { id: 'programs', name: 'Programs', icon: Layout },
  { id: 'donate', name: 'Donate', icon: Heart },
  { id: 'schedule', name: 'Schedule', icon: BarChart3 },
  { id: 'docs', name: 'Documentation', icon: FileText },
  { id: 'mission', name: 'Mission', icon: Heart },
  { id: 'trainers', name: 'Trainers', icon: Users }
];

// ==================== FONT OPTIONS ====================
const FONT_OPTIONS = [
  { id: 'inter', name: 'Inter', family: 'Inter, sans-serif', style: 'Modern & Clean' },
  { id: 'poppins', name: 'Poppins', family: 'Poppins, sans-serif', style: 'Friendly & Rounded' },
  { id: 'montserrat', name: 'Montserrat', family: 'Montserrat, sans-serif', style: 'Bold & Geometric' },
  { id: 'playfair', name: 'Playfair Display', family: 'Playfair Display, serif', style: 'Elegant & Classic' },
  { id: 'space', name: 'Space Grotesk', family: 'Space Grotesk, sans-serif', style: 'Tech & Futuristic' },
  { id: 'dm-sans', name: 'DM Sans', family: 'DM Sans, sans-serif', style: 'Simple & Readable' },
  { id: 'nunito', name: 'Nunito', family: 'Nunito, sans-serif', style: 'Warm & Approachable' },
  { id: 'raleway', name: 'Raleway', family: 'Raleway, sans-serif', style: 'Stylish & Thin' },
  { id: 'oswald', name: 'Oswald', family: 'Oswald, sans-serif', style: 'Strong & Impactful' },
  { id: 'lora', name: 'Lora', family: 'Lora, serif', style: 'Traditional & Readable' },
  { id: 'roboto', name: 'Roboto', family: 'Roboto, sans-serif', style: 'Universal & Neutral' },
  { id: 'merriweather', name: 'Merriweather', family: 'Merriweather, serif', style: 'Literary & Warm' }
];

// ==================== ANIMATION OPTIONS ====================
const ANIMATION_OPTIONS = [
  { id: 'none', name: 'None', description: 'No animations', preview: null },
  { id: 'fade', name: 'Fade In', description: 'Gentle fade effect', preview: 'opacity' },
  { id: 'fade-up', name: 'Fade Up', description: 'Fade while sliding up', preview: 'translate-y' },
  { id: 'fade-down', name: 'Fade Down', description: 'Fade while sliding down', preview: 'translate-y-reverse' },
  { id: 'slide-in', name: 'Slide In', description: 'Slide from the side', preview: 'translate-x' },
  { id: 'zoom', name: 'Zoom In', description: 'Scale up effect', preview: 'scale' },
  { id: 'bounce', name: 'Bounce', description: 'Playful bounce effect', preview: 'bounce' },
  { id: 'flip', name: 'Flip', description: '3D flip rotation', preview: 'rotate-x' },
  { id: 'parallax', name: 'Parallax', description: 'Depth scrolling effect', preview: 'parallax' }
];

// ==================== BACKDROP OPTIONS ====================
const BACKDROP_OPTIONS = [
  { id: 'solid', name: 'Solid Color', type: 'color' },
  { id: 'gradient', name: 'Gradient', type: 'gradient' },
  { id: 'pattern', name: 'Pattern', type: 'pattern' },
  { id: 'image', name: 'Image', type: 'image' },
  { id: 'animated', name: 'Animated', type: 'animated' }
];

const GRADIENT_PRESETS = [
  { id: 'sunset', name: 'Sunset', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'ocean', name: 'Ocean', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'forest', name: 'Forest', value: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { id: 'fire', name: 'Fire', value: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
  { id: 'sky', name: 'Sky', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'midnight', name: 'Midnight', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { id: 'rose', name: 'Rose', value: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)' },
  { id: 'gold', name: 'Gold', value: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)' }
];

const PATTERN_PRESETS = [
  { id: 'dots', name: 'Dots', css: 'radial-gradient(circle, #ffffff10 1px, transparent 1px)', size: '20px 20px' },
  { id: 'grid', name: 'Grid', css: 'linear-gradient(#ffffff10 1px, transparent 1px), linear-gradient(90deg, #ffffff10 1px, transparent 1px)', size: '40px 40px' },
  { id: 'diagonal', name: 'Diagonal', css: 'repeating-linear-gradient(45deg, #ffffff05, #ffffff05 10px, transparent 10px, transparent 20px)', size: '28px 28px' },
  { id: 'waves', name: 'Waves', css: 'radial-gradient(ellipse at 50% 0%, #ffffff10 0%, transparent 75%)', size: '100px 50px' }
];

// ==================== MASCOT OPTIONS ====================
const MASCOT_PRESETS = [
  { id: 'cat', name: 'Friendly Cat', icon: Cat, prompt: 'cute friendly cartoon cat mascot' },
  { id: 'dog', name: 'Happy Dog', icon: Dog, prompt: 'cute happy cartoon dog mascot' },
  { id: 'robot', name: 'Helpful Robot', icon: Bot, prompt: 'friendly helpful cartoon robot mascot' },
  { id: 'ghost', name: 'Cute Ghost', icon: Ghost, prompt: 'cute friendly cartoon ghost mascot' },
  { id: 'smile', name: 'Smiley', icon: Smile, prompt: 'cute cartoon smiley face character mascot' },
  { id: 'crown', name: 'Royal', icon: Crown, prompt: 'cute cartoon character with crown mascot' }
];

// ==================== COLOR SCHEMES ====================
const COLOR_SCHEMES = [
  { id: 'teal', name: 'Teal', primary: '#14b8a6', secondary: '#0d9488', accent: '#2dd4bf' },
  { id: 'blue', name: 'Ocean Blue', primary: '#3b82f6', secondary: '#2563eb', accent: '#60a5fa' },
  { id: 'purple', name: 'Royal Purple', primary: '#8b5cf6', secondary: '#7c3aed', accent: '#a78bfa' },
  { id: 'pink', name: 'Rose Pink', primary: '#ec4899', secondary: '#db2777', accent: '#f472b6' },
  { id: 'orange', name: 'Sunset Orange', primary: '#f97316', secondary: '#ea580c', accent: '#fb923c' },
  { id: 'green', name: 'Forest Green', primary: '#22c55e', secondary: '#16a34a', accent: '#4ade80' },
  { id: 'red', name: 'Bold Red', primary: '#ef4444', secondary: '#dc2626', accent: '#f87171' },
  { id: 'indigo', name: 'Deep Indigo', primary: '#6366f1', secondary: '#4f46e5', accent: '#818cf8' },
  { id: 'yellow', name: 'Sunny Yellow', primary: '#eab308', secondary: '#ca8a04', accent: '#facc15' },
  { id: 'cyan', name: 'Electric Cyan', primary: '#06b6d4', secondary: '#0891b2', accent: '#22d3ee' }
];

export default function WebsiteBuilderPro() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Builder State
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [createdSite, setCreatedSite] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Site Configuration
  const [siteConfig, setSiteConfig] = useState({
    businessName: '',
    businessDescription: '',
    industry: '',
    targetAudience: '',
    pages: ['home', 'about', 'services', 'contact'],
    navigation: [],
    colorScheme: COLOR_SCHEMES[0],
    font: FONT_OPTIONS[0],
    animation: ANIMATION_OPTIONS[2], // fade-up
    backdrop: { type: 'solid', value: '#0a0a0a' },
    mascot: null,
    mascotPosition: 'bottom-right'
  });

  // Template Category Filter
  const [categoryFilter, setCategoryFilter] = useState('All');
  const categories = ['All', ...new Set(VISUAL_TEMPLATES.map(t => t.category))];

  const filteredTemplates = categoryFilter === 'All' 
    ? VISUAL_TEMPLATES 
    : VISUAL_TEMPLATES.filter(t => t.category === categoryFilter);

  // Select template and apply its defaults
  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setSiteConfig(prev => ({
      ...prev,
      pages: template.pages,
      font: FONT_OPTIONS.find(f => f.name === template.style.font) || FONT_OPTIONS[0],
      animation: ANIMATION_OPTIONS.find(a => a.id === template.style.animation) || ANIMATION_OPTIONS[2]
    }));
  };

  // Toggle page selection
  const togglePage = (pageId) => {
    if (pageId === 'home') return; // Home is required
    setSiteConfig(prev => ({
      ...prev,
      pages: prev.pages.includes(pageId)
        ? prev.pages.filter(p => p !== pageId)
        : [...prev.pages, pageId]
    }));
  };

  // Add dropdown menu to navigation
  const addNavDropdown = (parentPage, childPages) => {
    setSiteConfig(prev => ({
      ...prev,
      navigation: [
        ...prev.navigation.filter(n => n.parent !== parentPage),
        { parent: parentPage, children: childPages }
      ]
    }));
  };

  // Generate mascot with AI
  const generateMascot = async (mascotType) => {
    if (!token) return;
    
    const preset = MASCOT_PRESETS.find(m => m.id === mascotType);
    if (!preset) return;
    
    try {
      // This would call the image generation API
      // For now, set the mascot type
      setSiteConfig(prev => ({
        ...prev,
        mascot: { type: mascotType, ...preset, generating: true }
      }));
      
      // Simulate generation (replace with actual API call)
      setTimeout(() => {
        setSiteConfig(prev => ({
          ...prev,
          mascot: { ...prev.mascot, generating: false, generated: true }
        }));
      }, 2000);
    } catch (err) {
      console.error('Failed to generate mascot:', err);
    }
  };

  // Generate the website
  const handleGenerate = async () => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/website-builder/pro/generate`, {
        business_name: siteConfig.businessName,
        business_description: siteConfig.businessDescription,
        industry: siteConfig.industry,
        target_audience: siteConfig.targetAudience,
        template_id: selectedTemplate?.id,
        pages: siteConfig.pages,
        navigation: siteConfig.navigation,
        color_scheme: siteConfig.colorScheme,
        font: siteConfig.font,
        animation: siteConfig.animation,
        backdrop: siteConfig.backdrop,
        mascot: siteConfig.mascot
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedSite(response.data);
      setStep(6); // Success step
    } catch (err) {
      console.error('Failed to create site:', err);
      alert('Failed to create site. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Wand2 className="w-16 h-16 text-teal-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Website Builder Pro</h2>
          <p className="text-zinc-400 mb-6">Sign in to create stunning websites with AI</p>
          <Button onClick={() => navigate('/auth')} className="bg-teal-500 hover:bg-teal-600">
            Sign In to Start
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        
        {/* Progress Steps */}
        {step < 6 && (
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 sm:gap-4 overflow-x-auto py-2">
              {[
                { num: 1, label: 'Template' },
                { num: 2, label: 'Details' },
                { num: 3, label: 'Pages' },
                { num: 4, label: 'Design' },
                { num: 5, label: 'Extras' }
              ].map((s, i) => (
                <div key={s.num} className="flex items-center">
                  <button
                    onClick={() => s.num < step && setStep(s.num)}
                    disabled={s.num > step}
                    className={`flex flex-col items-center ${s.num <= step ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      s.num < step ? 'bg-teal-500 text-white' :
                      s.num === step ? 'bg-teal-500/20 border-2 border-teal-500 text-teal-400' :
                      'bg-zinc-800 text-zinc-500'
                    }`}>
                      {s.num < step ? <Check className="w-5 h-5" /> : s.num}
                    </div>
                    <span className={`text-xs mt-1 hidden sm:block ${s.num === step ? 'text-teal-400' : 'text-zinc-500'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < 4 && <div className={`w-8 sm:w-12 h-0.5 mx-1 ${s.num < step ? 'bg-teal-500' : 'bg-zinc-800'}`} />}
                </div>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Step 1: Template Selection */}
            {step === 1 && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Choose a Template</h1>
                  <p className="text-zinc-400">Start with a professionally designed template</p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        categoryFilter === cat
                          ? 'bg-teal-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Template Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTemplates.map((template) => {
                    const isSelected = selectedTemplate?.id === template.id;
                    return (
                      <motion.button
                        key={template.id}
                        whileHover={{ scale: 1.02, y: -4 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => selectTemplate(template)}
                        className={`relative rounded-2xl overflow-hidden text-left transition-all ${
                          isSelected 
                            ? 'ring-2 ring-teal-500 ring-offset-2 ring-offset-zinc-950' 
                            : 'hover:ring-1 hover:ring-zinc-700'
                        }`}
                      >
                        {/* Preview */}
                        <div 
                          className="h-40 relative"
                          style={{ background: template.preview }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <MonitorSmartphone className="w-12 h-12 text-white/50" />
                          </div>
                          {isSelected && (
                            <div className="absolute top-3 right-3 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className="absolute top-3 left-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-black/30 text-white/80 backdrop-blur-sm">
                              {template.category}
                            </span>
                          </div>
                        </div>

                        {/* Info */}
                        <div className="p-4 bg-zinc-900">
                          <h3 className="text-white font-semibold mb-1">{template.name}</h3>
                          <p className="text-zinc-500 text-sm mb-3">{template.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {template.pages.slice(0, 4).map(page => (
                              <span key={page} className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 capitalize">
                                {page}
                              </span>
                            ))}
                            {template.pages.length > 4 && (
                              <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">
                                +{template.pages.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                <div className="flex justify-end mt-8">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedTemplate}
                    className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Business Details */}
            {step === 2 && (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Tell Us About Your Business</h1>
                  <p className="text-zinc-400">This helps our AI create perfect content for you</p>
                </div>

                <div className="space-y-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                  <div>
                    <label className="block text-zinc-300 mb-2 font-medium">Business Name *</label>
                    <Input
                      value={siteConfig.businessName}
                      onChange={(e) => setSiteConfig(prev => ({ ...prev, businessName: e.target.value }))}
                      placeholder="e.g., Acme Corp, Sarah's Bakery"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-zinc-300 mb-2 font-medium">What do you do? *</label>
                    <textarea
                      value={siteConfig.businessDescription}
                      onChange={(e) => setSiteConfig(prev => ({ ...prev, businessDescription: e.target.value }))}
                      placeholder="Describe your business, products, or services in 2-3 sentences..."
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-300 mb-2 font-medium">Industry</label>
                      <Input
                        value={siteConfig.industry}
                        onChange={(e) => setSiteConfig(prev => ({ ...prev, industry: e.target.value }))}
                        placeholder="e.g., Technology, Food & Beverage"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 mb-2 font-medium">Target Audience</label>
                      <Input
                        value={siteConfig.targetAudience}
                        onChange={(e) => setSiteConfig(prev => ({ ...prev, targetAudience: e.target.value }))}
                        placeholder="e.g., Small businesses, Young professionals"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(1)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!siteConfig.businessName || !siteConfig.businessDescription}
                    className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Pages & Navigation */}
            {step === 3 && (
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Choose Your Pages</h1>
                  <p className="text-zinc-400">Select which pages to include in your site</p>
                </div>

                {/* Selected Pages Preview */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Menu className="w-4 h-4 text-teal-400" />
                    <span className="text-zinc-300 font-medium">Your Navigation Menu</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {siteConfig.pages.map(pageId => {
                      const page = AVAILABLE_PAGES.find(p => p.id === pageId);
                      return (
                        <div key={pageId} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-500/20 border border-teal-500/30">
                          <page.icon className="w-4 h-4 text-teal-400" />
                          <span className="text-teal-300 text-sm">{page.name}</span>
                          {!page.required && (
                            <button 
                              onClick={() => togglePage(pageId)}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Available Pages */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {AVAILABLE_PAGES.map(page => {
                    const isSelected = siteConfig.pages.includes(page.id);
                    const Icon = page.icon;
                    return (
                      <button
                        key={page.id}
                        onClick={() => togglePage(page.id)}
                        disabled={page.required}
                        className={`p-4 rounded-xl text-left transition-all ${
                          isSelected
                            ? 'bg-teal-500/20 border-2 border-teal-500'
                            : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
                        } ${page.required ? 'cursor-not-allowed' : ''}`}
                      >
                        <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-teal-400' : 'text-zinc-500'}`} />
                        <p className={`font-medium ${isSelected ? 'text-white' : 'text-zinc-400'}`}>{page.name}</p>
                        {page.required && <span className="text-xs text-zinc-600">Required</span>}
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(2)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(4)} className="bg-teal-500 hover:bg-teal-600">
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Design Customization */}
            {step === 4 && (
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Design Your Site</h1>
                  <p className="text-zinc-400">Customize colors, fonts, and animations</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Color Scheme */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-5 h-5 text-teal-400" />
                      <h3 className="text-white font-semibold">Color Scheme</h3>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {COLOR_SCHEMES.map(scheme => (
                        <button
                          key={scheme.id}
                          onClick={() => setSiteConfig(prev => ({ ...prev, colorScheme: scheme }))}
                          className={`aspect-square rounded-xl transition-all hover:scale-110 ${
                            siteConfig.colorScheme.id === scheme.id ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900' : ''
                          }`}
                          style={{ backgroundColor: scheme.primary }}
                          title={scheme.name}
                        />
                      ))}
                    </div>
                    <p className="text-zinc-500 text-sm mt-3">Selected: {siteConfig.colorScheme.name}</p>
                  </div>

                  {/* Font Selection */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Type className="w-5 h-5 text-teal-400" />
                      <h3 className="text-white font-semibold">Font Style</h3>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                      {FONT_OPTIONS.map(font => (
                        <button
                          key={font.id}
                          onClick={() => setSiteConfig(prev => ({ ...prev, font }))}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            siteConfig.font.id === font.id
                              ? 'bg-teal-500/20 border border-teal-500'
                              : 'bg-zinc-800 hover:bg-zinc-700'
                          }`}
                        >
                          <p className="text-white font-medium" style={{ fontFamily: font.family }}>{font.name}</p>
                          <p className="text-zinc-500 text-xs">{font.style}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Animations */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Move className="w-5 h-5 text-teal-400" />
                      <h3 className="text-white font-semibold">Animations</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {ANIMATION_OPTIONS.map(anim => (
                        <button
                          key={anim.id}
                          onClick={() => setSiteConfig(prev => ({ ...prev, animation: anim }))}
                          className={`p-3 rounded-lg text-center transition-all ${
                            siteConfig.animation.id === anim.id
                              ? 'bg-teal-500/20 border border-teal-500'
                              : 'bg-zinc-800 hover:bg-zinc-700'
                          }`}
                        >
                          <p className="text-white text-sm font-medium">{anim.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Backdrop */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <PaintBucket className="w-5 h-5 text-teal-400" />
                      <h3 className="text-white font-semibold">Background</h3>
                    </div>
                    
                    {/* Backdrop Type */}
                    <div className="flex gap-2 mb-4">
                      {BACKDROP_OPTIONS.slice(0, 3).map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setSiteConfig(prev => ({ 
                            ...prev, 
                            backdrop: { ...prev.backdrop, type: opt.type }
                          }))}
                          className={`flex-1 py-2 rounded-lg text-sm ${
                            siteConfig.backdrop.type === opt.type
                              ? 'bg-teal-500 text-white'
                              : 'bg-zinc-800 text-zinc-400'
                          }`}
                        >
                          {opt.name}
                        </button>
                      ))}
                    </div>

                    {/* Gradient Presets */}
                    {siteConfig.backdrop.type === 'gradient' && (
                      <div className="grid grid-cols-4 gap-2">
                        {GRADIENT_PRESETS.map(grad => (
                          <button
                            key={grad.id}
                            onClick={() => setSiteConfig(prev => ({
                              ...prev,
                              backdrop: { type: 'gradient', value: grad.value }
                            }))}
                            className="aspect-square rounded-lg transition-all hover:scale-110"
                            style={{ background: grad.value }}
                            title={grad.name}
                          />
                        ))}
                      </div>
                    )}

                    {/* Color Picker */}
                    {siteConfig.backdrop.type === 'color' && (
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={siteConfig.backdrop.value || '#0a0a0a'}
                          onChange={(e) => setSiteConfig(prev => ({
                            ...prev,
                            backdrop: { type: 'color', value: e.target.value }
                          }))}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <span className="text-zinc-400 text-sm">Pick a solid background color</span>
                      </div>
                    )}

                    {/* Pattern Presets */}
                    {siteConfig.backdrop.type === 'pattern' && (
                      <div className="grid grid-cols-4 gap-2">
                        {PATTERN_PRESETS.map(pattern => (
                          <button
                            key={pattern.id}
                            onClick={() => setSiteConfig(prev => ({
                              ...prev,
                              backdrop: { type: 'pattern', value: pattern }
                            }))}
                            className="aspect-square rounded-lg bg-zinc-800 transition-all hover:scale-110"
                            style={{ 
                              backgroundImage: pattern.css,
                              backgroundSize: pattern.size
                            }}
                            title={pattern.name}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(3)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(5)} className="bg-teal-500 hover:bg-teal-600">
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Extras (Mascot) */}
            {step === 5 && (
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Add Extras</h1>
                  <p className="text-zinc-400">Optional: Add a mascot or special features</p>
                </div>

                {/* Mascot Selection */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Smile className="w-5 h-5 text-teal-400" />
                      <h3 className="text-white font-semibold">Site Mascot</h3>
                    </div>
                    {siteConfig.mascot && (
                      <button
                        onClick={() => setSiteConfig(prev => ({ ...prev, mascot: null }))}
                        className="text-zinc-500 hover:text-red-400 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-zinc-500 text-sm mb-4">Add a friendly mascot to your site (uses 1 image credit)</p>
                  
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {MASCOT_PRESETS.map(mascot => {
                      const Icon = mascot.icon;
                      const isSelected = siteConfig.mascot?.id === mascot.id;
                      return (
                        <button
                          key={mascot.id}
                          onClick={() => generateMascot(mascot.id)}
                          className={`p-4 rounded-xl text-center transition-all ${
                            isSelected
                              ? 'bg-teal-500/20 border-2 border-teal-500'
                              : 'bg-zinc-800 hover:bg-zinc-700'
                          }`}
                        >
                          <Icon className={`w-8 h-8 mx-auto mb-2 ${isSelected ? 'text-teal-400' : 'text-zinc-400'}`} />
                          <p className={`text-xs ${isSelected ? 'text-white' : 'text-zinc-500'}`}>{mascot.name}</p>
                        </button>
                      );
                    })}
                  </div>

                  {siteConfig.mascot && (
                    <div className="mt-4 p-4 rounded-lg bg-zinc-800">
                      <p className="text-zinc-300 text-sm">
                        {siteConfig.mascot.generating 
                          ? '✨ Generating your mascot...' 
                          : `✓ ${siteConfig.mascot.name} mascot will be added to your site`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Preview Summary */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                  <h3 className="text-white font-semibold mb-4">Site Summary</h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500">Business</p>
                      <p className="text-white">{siteConfig.businessName}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Template</p>
                      <p className="text-white">{selectedTemplate?.name}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Pages</p>
                      <p className="text-white">{siteConfig.pages.length} pages</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Style</p>
                      <p className="text-white">{siteConfig.font.name} • {siteConfig.colorScheme.name}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(4)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowPreview(true)}
                      variant="outline"
                      className="border-teal-500/50 text-teal-400"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                    >
                      {generating ? (
                        <>
                          <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Rocket className="w-4 h-4 mr-2" />
                          Create My Website
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Success */}
            {step === 6 && createdSite && (
              <div className="max-w-xl mx-auto text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-24 h-24 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Globe className="w-12 h-12 text-teal-400" />
                </motion.div>

                <h1 className="text-3xl font-bold text-white mb-2">Your Website is Ready! 🎉</h1>
                <p className="text-zinc-400 mb-8">{createdSite.message || 'Your site has been created successfully'}</p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/my-websites')}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View My Websites
                  </Button>
                  <Button
                    onClick={() => {
                      setStep(1);
                      setSelectedTemplate(null);
                      setCreatedSite(null);
                      setSiteConfig({
                        businessName: '',
                        businessDescription: '',
                        industry: '',
                        targetAudience: '',
                        pages: ['home', 'about', 'services', 'contact'],
                        navigation: [],
                        colorScheme: COLOR_SCHEMES[0],
                        font: FONT_OPTIONS[0],
                        animation: ANIMATION_OPTIONS[2],
                        backdrop: { type: 'solid', value: '#0a0a0a' },
                        mascot: null,
                        mascotPosition: 'bottom-right'
                      });
                    }}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Another
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
